import { Injectable } from '@angular/core';
import { MatDialog, MatDialogConfig } from '@angular/material/dialog';
import { Cargo } from 'src/app/core/interfaces/cargo';
import { Vehicle } from 'src/app/core/interfaces/vehicle';
import { ConsignmentCargo } from 'src/app/core/interfaces/consignmentCargo';
import { PermissionRole } from 'src/app/core/resources/permission-role';
import { Permission } from 'src/app/core/resources/permission';
import { Utils } from 'src/app/core/resources/utils';
import { CargoStateEnum } from 'src/app/core/enums/cargoState.enum';
import { MenuServiceService } from 'src/app/modules/service-request-form/menu-service/menu-service.service';
import { CargoValidationModalComponent } from 'src/app/modules/cargo/cargo-validation-modal/cargo-validation-modal.component';

// Importar todos los componentes necesarios
import { ContractorTripInfoComponent, ContractorTripInfoDialogSection } from 'src/app/modules/service-request-form/components/contractor-trip-info/contractor-trip-info.component';
import { OriginLoadServiceFormComponent } from 'src/app/modules/service-request-form/components/origin-load-service-form/origin-load-service-form.component';
import { DestinationDownloadServiceFormComponent } from 'src/app/modules/service-request-form/components/destination-download-service-form/destination-download-service-form.component';
import { VehicleServiceFormComponent } from 'src/app/modules/service-request-form/components/vehicle-service-form/vehicle-service-form.component';
import { EscortedVehicleFormComponent } from 'src/app/modules/service-request-form/components/escorted-vehicle-form/escorted-vehicle-form.component';
import { LoadTypeServiceFormComponent } from 'src/app/modules/service-request-form/components/load-type-service-form/load-type-service-form.component';
import { LoadCharacteristicsFormComponent } from 'src/app/modules/service-request-form/components/load-characteristics-form/load-characteristics-form.component';
import { AdditionalValuesServiceFormComponent } from 'src/app/modules/service-request-form/components/additional-values-service-form/additional-values-service-form.component';
import { ObservationsServiceFormComponent } from 'src/app/modules/service-request-form/components/observations-service-form/observations-service-form.component';
import { ConsignmentsServiceFormComponent } from 'src/app/modules/service-request-form/components/consignments-service-form/consignments-service-form.component';
import { ServicePublicationFormComponent } from 'src/app/modules/service-request-form/components/service-publication-form/service-publication-form.component';
import { ModalEnum } from '../enums/modal.enum';
import { SatelliteLockServiceFormComponent } from 'src/app/modules/service-request-form/components/satellite-lock-service-form/satellite-lock-service-form.component';

export interface SectionValidation {
    name: string;
    component: any;
    checkValid: { isValid: boolean, error?: string };
    canEdit: { restriction: boolean, message: string }[];
    dialogData?: { [key: string]: any };
}

@Injectable({
    providedIn: 'root'
})
export class ServiceValidationManager {
    permission = Permission;

    constructor(
        private permissionRole: PermissionRole,
        private utils: Utils,
        private menuService: MenuServiceService,
        private dialog: MatDialog
    ) { }

    /**
     * @description Obtiene las secciones de edición con validaciones integradas
     * @param cargo - La carga a validar
     * @param vehicle - El vehículo asociado
     * @param consignments - Array de remesas
     * @returns Objeto con las secciones y sus validaciones
     */
    public getSectionsWithValidation(
        cargo: Cargo,
        vehicle?: Vehicle,
        consignments?: ConsignmentCargo[],
        ignorePermission?: boolean
    ): { [key: string]: SectionValidation } {
        const sections = {
            contractorInformation: {
                name: this.menuService.requiresTransportCompany(cargo)
                    ? 'Contratante y empresa de transporte'
                    : 'Información del contratante',
                component: ContractorTripInfoComponent,
                dialogData: { contractorTripInfoSection: ContractorTripInfoDialogSection.CONTRACTOR_TRANSPORT },
                checkValid: this.menuService.isValidContractorDetails(cargo),
                canEdit: [{
                    restriction: this.permissionRole.hasPermission(this.permission.cargo.module, this.permission.cargo.editCargo),
                    message: 'No tienes permisos para editar el contratante'
                }]
            },
            contractorAdditionalInformation: {
                name: 'Información adicional del servicio',
                component: ContractorTripInfoComponent,
                dialogData: { contractorTripInfoSection: ContractorTripInfoDialogSection.ADDITIONAL_INFORMATION },
                checkValid: this.menuService.isValidContractorAdditionalInformation(cargo),
                canEdit: [{
                    restriction: this.permissionRole.hasPermission(this.permission.cargo.module, this.permission.cargo.editCargo)
                        || this.permissionRole.hasPermission(this.permission.cargo.module, this.permission.cargo.editCargoTags),
                    message: 'No tienes permisos para editar la información del contratante'
                }]
            },
            origin: {
                name: 'Origen del servicio',
                component: OriginLoadServiceFormComponent,
                checkValid: this.menuService.isValidOrigin(cargo, vehicle),
                canEdit: [{
                    restriction: this.permissionRole.hasPermission(this.permission.cargo.module, this.permission.cargo.editLoadDownloadModule),
                    message: 'No tienes permisos para editar el origen del servicio'
                }, {
                    restriction: [CargoStateEnum.ACCEPTED, CargoStateEnum.CREATED].includes(cargo.state) ||
                        ((!cargo.ministry || (cargo.authorizedWithoutManifest && !cargo.manifestAuthorization)) && cargo.state === CargoStateEnum.START_SERVICE),
                    message: 'No se puede editar el origen del servicio, ya que el servicio ya ha iniciado'
                }, {
                    restriction: !this.utils.getNestedValue(cargo, 'cargoFeature.uploadDownload.destination.0.addresses.0.state'),
                    message: 'Ya que se han recorrido todas las direcciones de origen'
                }, {
                    restriction: !cargo.consecutiveMainCargo,
                    message: 'No es posible editar el origen de un servicio consolidado'
                }, {
                    restriction: !cargo.manifestAuthorization,
                    message: 'El servicio ya tiene un manifiesto generado, no se puede editar esta sección'
                }]
            },
            destination: {
                name: 'Destino del servicio',
                component: DestinationDownloadServiceFormComponent,
                checkValid: this.menuService.isValidDestination(cargo, consignments),
                canEdit: [{
                    restriction: this.permissionRole.hasPermission(this.permission.cargo.module, this.permission.cargo.editLoadDownloadModule),
                    message: 'No tienes permisos para editar el destino del servicio'
                }, {
                    restriction: [CargoStateEnum.ACCEPTED, CargoStateEnum.CREATED].includes(cargo.state) ||
                        ((!cargo.ministry || (cargo.authorizedWithoutManifest && !cargo.manifestAuthorization)) && cargo.state === CargoStateEnum.START_SERVICE),
                    message: 'No se puede editar el destino del servicio, ya que el servicio ya ha iniciado'
                }, {
                    restriction: !cargo.consecutiveMainCargo,
                    message: 'No es posible editar el destino de un servicio consolidado'
                }, {
                    restriction: !cargo.manifestAuthorization,
                    message: 'El servicio ya tiene un manifiesto generado, no se puede editar esta sección'
                }]
            },
            vehicleType: {
                name: 'Tipo de vehículo',
                component: VehicleServiceFormComponent,
                checkValid: this.menuService.isValidVehicleTypeInfo(cargo),
                canEdit: [{
                    restriction: this.permissionRole.hasPermission(this.permission.cargo.module, this.permission.cargo.editTypeVehicle),
                    message: 'No tienes permisos para editar el tipo de vehículo'
                }, {
                    restriction: !cargo.assignedDriver && !cargo.licensePlate && !cargo.driver,
                    message: 'El servicio tiene un vehículo asignado, debes desasociarlo para poder editar esta sección'
                }]
            },
            escortedVehicle: {
                name: 'Vehículo escoltado',
                component: EscortedVehicleFormComponent,
                checkValid: this.menuService.isValidEscortedVehicle(cargo),
                canEdit: [{
                    restriction: this.permissionRole.hasPermission(this.permission.cargo.module, this.permission.cargo.editCargo),
                    message: 'No tienes permisos para editar el vehículo escoltado'
                }]
            },
            serviceType: {
                name: 'Tipo de servicio',
                component: LoadTypeServiceFormComponent,
                checkValid: this.menuService.isValidLoadTypeService(cargo),
                canEdit: [{
                    restriction: this.permissionRole.hasPermission(this.permission.cargo.module, this.permission.cargo.editServiceTypeModule),
                    message: 'No tienes permisos para editar el tipo de servicio'
                }, {
                    restriction: !cargo.manifestAuthorization,
                    message: 'El servicio ya tiene un manifiesto generado, no se puede editar esta sección'
                }]
            },
            cargoCharacteristics: {
                name: 'Características del servicio',
                component: LoadCharacteristicsFormComponent,
                checkValid: this.menuService.isValidLoadCharacteristics(cargo),
                canEdit: [{
                    restriction: this.permissionRole.hasPermission(this.permission.cargo.module, this.permission.cargo.editServiceCharacteristicsModule),
                    message: 'No tienes permisos para editar las características del servicio'
                }, {
                    restriction: !cargo.confirmedDriver || !this.utils.isEscortedService(cargo),
                    message: 'Ya hay un escolta confirmado para este servicio, no es posible editar esta sección'
                }]
            },
            satelliteLock: {
                name: 'Candado satelital',
                component: SatelliteLockServiceFormComponent,
                checkValid: this.menuService.isValidSatelliteLock(cargo),
                canEdit: [{
                    restriction: this.permissionRole.hasPermission(this.permission.cargo.module, this.permission.cargo.editSatelliteLockModule),
                    message: 'No tienes permisos para editar el candado satelital'
                }, {
                    restriction: !this.utils.getNestedValue(cargo, 'lock.imei'),
                    message: 'Ya hay un candado satelital asignado para este servicio, no es posible editar esta sección'
                }]
            },
            serviceValue: {
                name: 'Valor del servicio',
                component: AdditionalValuesServiceFormComponent,
                checkValid: this.menuService.isValidServiceValue(cargo, vehicle, ignorePermission),
                canEdit: [{
                    restriction: this.permissionRole.hasPermission(this.permission.cargo.module, this.permission.cargo.showEditFinancialInformationModule) || (
                        [CargoStateEnum.CREATED, CargoStateEnum.ACCEPTED].includes(cargo.state) && this.permissionRole.hasPermission(this.permission.cargo.module, this.permission.cargo.reduceScheduledTransportFreightCost)
                    ),
                    message: 'No tienes permisos para editar el valor del servicio'
                }, {
                    restriction: !this.menuService.isSatelliteLockService(cargo),
                    message: 'No es posible editar esta sección'
                }, {
                    restriction: !this.utils.getNestedValue(cargo, 'shippingCost.advanceState') || this.utils.getNestedValue(cargo, 'shippingCost.advancePercentage') === 0,
                    message: 'Ya se ha pagado el anticipo, no se puede editar esta sección'
                }, {
                    restriction: !cargo.manifestAuthorization,
                    message: 'El servicio ya tiene un manifiesto generado, no se puede editar esta sección'
                }]
            },
            observations: {
                name: 'Observaciones',
                component: ObservationsServiceFormComponent,
                checkValid: { isValid: true, error: '' }, // Las observaciones no tienen validaciones específicas
                canEdit: [{
                    restriction: this.permissionRole.hasPermission(this.permission.cargo.module, this.permission.cargo.addObservation),
                    message: 'No tienes permisos para editar observaciones'
                }]
            },
            manifest: {
                name: 'Manifiesto y remesas',
                component: ConsignmentsServiceFormComponent,
                checkValid: this.menuService.isValidConsignmentsService(cargo, consignments),
                canEdit: [{
                    restriction: this.permissionRole.hasPermission(this.permission.cargo.module, this.permission.cargo.editConsignmentServiceModule),
                    message: 'No tienes permisos para editar el manifiesto y remesas'
                }, {
                    restriction: !cargo.manifestAuthorization,
                    message: 'El servicio ya tiene un manifiesto generado'
                }]
            },
            publishZone: {
                name: 'Zona de publicación',
                component: ServicePublicationFormComponent,
                checkValid: this.menuService.isValidServicePublication(cargo),
                canEdit: [{
                    restriction: this.permissionRole.hasPermission(this.permission.cargo.module, this.permission.cargo.editPublicationZone),
                    message: 'No tienes permisos para editar la zona de publicación'
                }, {
                    restriction: !cargo.confirmedDriver,
                    message: 'Ya hay un conductor confirmado para este servicio, no es posible editar esta sección'
                }]
            }
        };

        if (this.utils.isEscortedService(cargo) || this.utils.isSatelliteLockService(cargo)) {
            delete sections.serviceType;
            delete sections.publishZone;
        } else delete sections.escortedVehicle;
        if (!cargo.ministry)
            delete sections.manifest;

        return sections;
    }

    /**
     * @description Valida todas las secciones y abre el modal si hay errores
     * @param cargo - La carga a validar
     * @param vehicle - El vehículo asociado
     * @param consignments - Array de remesas
     * @param onNavigateToSection - Callback opcional para manejar la navegación a una sección
     * @returns Promise<boolean> - true si todas las secciones son válidas, false si hay errores
     */
    public async validateAllSectionsAndShowModal(
        cargo: Cargo,
        vehicle?: Vehicle,
        consignments?: ConsignmentCargo[],
        validateTripTimes: boolean = false
    ): Promise<boolean> {
        const sections = this.getSectionsWithValidation(cargo, vehicle, consignments, true);
        if (sections['destination'].checkValid.isValid && validateTripTimes) {
            const checkTripTimes = await this.menuService.checkTripTimes(cargo, true, true);
            if (!checkTripTimes.valid) {
                sections['destination'].checkValid.isValid = false;
                sections['destination'].checkValid.error = checkTripTimes.error;
            }
        }
        const allSectionsValid = Object.values(sections).every(section => section.checkValid.isValid);
        if (allSectionsValid) return true;

        const dialogConfig = new MatDialogConfig();
        dialogConfig.data = {
            cargo: cargo,
            sections: sections
        };
        dialogConfig.maxHeight = ModalEnum.MAX_HEIGHT;
        dialogConfig.maxWidth = ModalEnum.MAX_WIDTH;
        dialogConfig.width = ModalEnum.LARGE_WIDTH;
        dialogConfig.autoFocus = false;
        let dialogRef = this.dialog.open(CargoValidationModalComponent, dialogConfig);
        const result = await dialogRef.afterClosed().toPromise();
        if (!result) return false;
        dialogConfig.data = {
            cargo: cargo,
            ...(sections[result].dialogData || {})
        };
        dialogRef = this.dialog.open(sections[result].component, dialogConfig);
        return false;
    }
}
